import * as React from "react"
import { useSize } from "@/hooks/use-size"
import { cn } from "@/lib/utils"
import {
    buildSrcSet,
    buildTransformUrl,
    DEFAULT_TRANSFORM_WIDTH,
    getOriginalImageUrl,
    IMAGE_LOAD_MODE,
    nextImageLoadMode,
    parseWixMediaUrl,
} from "./image-helpers"

const FALLBACK_IMAGE_URL =
    "/images/fallback.webp"

const ImageWrapper = /** @type {any} */ (React.forwardRef(/** @param {any} props */({ aspectRatio, className, style, children }, ref) => (
    <span
        ref={ref}
        className={cn("inline-block relative", className)}
        style={{ aspectRatio, ...style }}
    >
        {children}
    </span>
)))
ImageWrapper.displayName = "ImageWrapper"

const ResponsiveImage = /** @type {any} */ (React.forwardRef(
    /** @param {any} props */({ parsed, fittingType, focalPoint, quality, className, style, aspectRatio, onLoad, ...props }, parentRef) => {
        const wrapperRef = React.useRef(null)
        const imgRef = React.useRef(null)
        const size = useSize(wrapperRef)
        const [loaded, setLoaded] = React.useState(false)

        React.useImperativeHandle(parentRef, () => imgRef.current)

        // Reset the blur-up when the underlying image changes.
        React.useEffect(() => {
            setLoaded(false)
        }, [parsed.baseUrl])

        const crop = fittingType !== "fit"
        // `size` is null exactly once: the pre-measurement first render, which we
        // never let reach the network (see below — useSize measures before paint).
        // A *measured* zero (content-sized wrapper with no CSS dimensions) falls
        // back to a fixed transform width so the image itself can size the box.
        const options = size && {
            width: size.width || DEFAULT_TRANSFORM_WIDTH,
            height: size.height ? size.height : undefined,
            crop,
            focalPoint: crop ? focalPoint : undefined,
            quality,
        }

        // Both layers render only once the container is measured, so the first
        // URL the browser ever fetches is already the right size — never a
        // DEFAULT_TRANSFORM_WIDTH guess that gets replaced a frame later (a
        // wasted full-size download per image). useSize measures in
        // useLayoutEffect, so nothing is lost: measurement lands before the
        // first paint.
        return (
            <ImageWrapper ref={wrapperRef} aspectRatio={aspectRatio} className={className} style={style}>
                {/* Tiny blurred placeholder (a few hundred bytes) covering the main
            image's load time. Same crop shape and focal anchor as the main
            image — fp_ is relative to the crop box, so a square or centered
            placeholder would blur-preview a different region. */}
                {options && !loaded && (
                    <img
                        src={buildTransformUrl(parsed, {
                            ...options,
                            width: 20,
                            height: options.height
                                ? Math.max(1, Math.round((20 * options.height) / options.width))
                                : undefined,
                            quality: 20,
                        })}
                        alt=""
                        aria-hidden="true"
                        className="w-full h-full inset-0 absolute"
                        style={{
                            objectFit: fittingType === "fit" ? "contain" : "cover",
                            filter: "blur(10px)",
                            transform: "scale(1.1)",
                        }}
                    />
                )}
                {options && (
                    <img
                        ref={imgRef}
                        src={buildTransformUrl(parsed, options)}
                        srcSet={buildSrcSet(parsed, options)}
                        loading={props.priority || props.loading === "eager" ? "eager" : (props.loading || "lazy")}
                        fetchPriority={props.priority || props.fetchPriority === "high" ? "high" : undefined}
                        className={cn(
                            "w-full h-full inset-0 absolute",
                            fittingType === "fit" ? "object-contain" : "object-cover"
                        )}
                        onLoad={(e) => {
                            setLoaded(true)
                            onLoad?.(e)
                        }}
                        {...props}
                    />
                )}
            </ImageWrapper>
        )
    }
))
ResponsiveImage.displayName = "ResponsiveImage"

/**
 * Image with built-in Wix Media Platform support: canonical public images on
 * media.base44.com and static.wixstatic.com/media are resized to the rendered
 * container per device pixel ratio and re-encoded to WebP; `fittingType="fill"`
 * crops server-side, optionally anchored at a focal point. Other URLs render
 * as a plain <img>. Failed transforms retry the original URL; only a broken
 * original swaps to the generic fallback image.
 */
const Image = /** @type {any} */ (React.forwardRef(
    /** @param {any} props */(
    {
        src,
        fittingType = "fill",
        originWidth,
        originHeight,
        focalPointX,
        focalPointY,
        quality = 90,
        onError,
        ...props
    },
    ref
) => {
        const parsedSource = src && src !== FALLBACK_IMAGE_URL ? parseWixMediaUrl(src) : null
        const initialMode = parsedSource ? IMAGE_LOAD_MODE.OPTIMIZED : IMAGE_LOAD_MODE.ORIGINAL
        const [loadState, setLoadState] = React.useState({ src, mode: initialMode })
        const mode = loadState.src === src ? loadState.mode : initialMode

        React.useEffect(() => {
            setLoadState({ src, mode: initialMode })
        }, [src, initialMode])

        const handleError = (event) => {
            if (mode === IMAGE_LOAD_MODE.FALLBACK) return
            const nextMode = nextImageLoadMode(mode)
            setLoadState({ src, mode: nextMode })
            if (nextMode === IMAGE_LOAD_MODE.FALLBACK) onError?.(event)
        }

        const imageProps = {
            ...props,
            onError: handleError,
        }

        if (!src) {
            // Renders as a real <img> (not a <div>) — the visual editor's
            // click-to-edit toolbar keys its "Replace Image" action off the DOM
            // tag being `img`, so a placeholder div would be unrecoverable in the
            // editor. FALLBACK_IMAGE_URL doubles as the "no image chosen" graphic.
            return <img ref={ref} src={FALLBACK_IMAGE_URL} {...imageProps} data-empty-image />
        }

        // A failed transform retries the underlying original as a plain image.
        // Only a failure of that original advances to the generic fallback.
        const parsed = mode === IMAGE_LOAD_MODE.OPTIMIZED ? parsedSource : null

        if (!parsed) {
            const isErrorMode = mode === IMAGE_LOAD_MODE.FALLBACK
            const imageSrc = isErrorMode ? FALLBACK_IMAGE_URL : getOriginalImageUrl(src, parsedSource)
            return (
                <img ref={ref} src={imageSrc} {...imageProps} data-error-image={isErrorMode || undefined} />
            )
        }

        const focalPoint =
            typeof focalPointX === "number" && typeof focalPointY === "number"
                ? { x: focalPointX, y: focalPointY }
                : undefined
        // Origin dimensions are optional — when known they stabilize layout via
        // the wrapper's aspect-ratio before the image loads.
        const aspectRatio =
            originWidth && originHeight ? `${originWidth} / ${originHeight}` : undefined

        return (
            <ResponsiveImage
                ref={ref}
                parsed={parsed}
                fittingType={fittingType}
                focalPoint={focalPoint}
                quality={quality}
                aspectRatio={aspectRatio}
                {...imageProps}
            />
        )
    }
))
Image.displayName = "Image"

export { Image }
